import { Button } from "@mui/material"
import FormikCheckboxField from "common/FormikCheckboxField"
import UseFocusFirstInvalidField from "common/UseFocusFirstInvalidField"
import { trackCheckoutStep } from "common/analytics"
import { regex } from "common/bit/form/FormikSchema"
import { Font } from "components/shopFeatures/wizard/constants"
import { Field, Form, Formik } from "formik"
import { TextField } from "formik-mui"
import { useEffect, useMemo, useRef } from "react"
import FormSelect, { toFormSelectOptions } from "reusable/formsComp/FormSelect"
import routes from "routes"
import { useAppDispatch, useTypedSelector } from "store"
import styled from "styled-components"
import * as yup from "yup"
import { register } from "../authSlice"

const StyledButton = styled(Button)`
  margin-top: ${(p) => p.theme.spacing(3)}px;
  background: black;
  height: 45px;
  border-radius: 3px;

  :hover {
    background: ${(p) => p.theme.colors.steel};
    color: ${(p) => p.theme.colors.white};
    outline: none;
    border: none;
  }
`

const StyledInfoText = styled.p`
  font-family: ${Font.OENaPro};
  font-size: 12px;
  font-weight: 300;
  line-height: 16px;
  letter-spacing: 0em;
  color: ${(p) => p?.theme?.colors?.mazdaRed};
`

const StyledField = styled(Field)`
  margin-top: 30px;

  :first-child {
    margin-top: 25px;
  }
`

const StyledFormikCheckboxField = styled(FormikCheckboxField)`
  .MuiFormControlLabel-label {
    font-family: ${Font.OENaPro};
    font-size: 12px;
    font-weight: 300;
    line-height: 16px;
    letter-spacing: 0em;
    text-align: right;
    padding-top: 6px;
  }
`

const labelStyles = {
  fontFamily: "OENaPRo",
  fontSize: "12px",
  fontWeight: "300",
  lineHeight: "16px",
  letterSpacing: "0em",
  textAlign: "right",
}

export type PhoneFormValues = {
  firstName: string
  lastName: string
  showRoom: number
  agreeMarketingOffers: boolean
}

const initialValues: PhoneFormValues = {
  firstName: "",
  lastName: "",
  showRoom: 0,
  agreeMarketingOffers: false,
}

const schema = yup
  .object({
    firstName: yup
      .string()
      .required("יש להזין שם מלא")
      .matches(regex.singleName, "שם פרטי בבקשה"),
    lastName: yup
      .string()
      .required("יש להזין שם מלא")
      .matches(regex.singleName, "שם משפחה בבקשה"),
    showRoom: yup.number().positive("יש לבחור אולם תצוגה").defined(),
    agreeMarketingOffers: yup.boolean().defined(),
  })
  .defined()

const RegisterForm = () => {
  const { loading } = useTypedSelector((state) => state.auth)
  const { showRooms } = useTypedSelector((state) => state.showRooms)
  const showRoomsOptions = toFormSelectOptions(showRooms, "id", "name")

  const dispatch = useAppDispatch()

  const firstNameRef = useRef<HTMLInputElement>(null)
  const lastNameRef = useRef<HTMLInputElement>(null)
  const showRoomRef = useRef<HTMLSelectElement>(null)

  const fieldNameInputRefMap = useMemo(
    () => ({
      firstName: firstNameRef,
      lastName: lastNameRef,
      showRoom: showRoomRef,
    }),
    []
  )

  useEffect(() => {
    if (location.hash === `#${routes.summary.path}`) trackCheckoutStep("3")
  }, [])

  return (
    <Formik
      validationSchema={schema}
      initialValues={initialValues}
      onSubmit={(values) =>
        dispatch(
          register({
            firstName: values.firstName,
            lastName: values.lastName,
            showRoom: values.showRoom,
            agreeMarketingOffers: values.agreeMarketingOffers,
          })
        )
      }
    >
      {() => {
        return (
          <>
            <UseFocusFirstInvalidField
              fieldNameInputRefMap={fieldNameInputRefMap}
            />

            <StyledInfoText>*שדות חובה</StyledInfoText>

            <Form noValidate aria-label="טופס הרשמה">
              <StyledField
                name="firstName"
                id="firstName"
                component={TextField}
                required
                autoComplete="given-name"
                helperText=" " // prevent a "jump" when an error appears
                inputRef={firstNameRef}
                placeholder="*שם פרטי"
                placeholderTextColor="red"
              />

              <StyledField
                name="lastName"
                id="lastName"
                component={TextField}
                required
                autoComplete="family-name"
                helperText=" " // prevent a "jump" when an error appears
                inputRef={lastNameRef}
                placeholder="*שם משפחה"
              />

              <FormSelect
                name="showRoom"
                id="showRoom"
                required
                label="*אולם תצוגה מועדף"
                placeholder="*אולם תצוגה מועדף"
                values={showRoomsOptions}
                map={(item) => ({ id: item.id, name: item.name })}
              />

              {/* Keeping for historical purposes
              <ShowRoomSelect inputRef={showRoomRef} /> */}

              <StyledFormikCheckboxField
                name="agreeMarketingOffers"
                label="אני רוצה לקבל פניות ועדכונים במייל ו/או בווטסאפ ו/או בסמס מקבוצת דלק מוטורס על מבצעים, השקות, אירועים ועוד. ולהיכלל במאגר המידע של הקבוצה."
                labelStyles={labelStyles}
              />

              {/* TODO: Use LoadingButto

              > label {
                font-size: 10px;
              }n */}
              <StyledButton
                fullWidth
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
                title="לחץ להרשמה או לחץ על מקש ESC לסגירת החלונית"
              >
                סיום הרשמה
              </StyledButton>
            </Form>
          </>
        )
      }}
    </Formik>
  )
}

export default RegisterForm
